// @flow
import React, {useMemo, useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {
  formatAddressString,
  getAddressFromShippingDetail,
} from '../../map/utils';

import {
  mapPaymentMethodName,
  ORDER_STATES,
  ORDER_STATE_COLORS,
  ORDER_STATE_STRINGS,
} from '../../../entities';
import type {
  Order,
  OrderState,
  PaymentMethod,
  Currency,
  Collaborator,
  StoreStockSettings,
} from '../../../entities';
import {
  ArrowBackIosRounded,
  ArrowForwardIosRounded,
  AttachFile,
  DeleteOutline,
  FileCopyOutlined,
  InfoOutlined,
} from '@material-ui/icons';
import {getImageUrl} from '../../../utils/image_provider';
import {commaFormat} from '../../../utils/number';
import type {OrderRequest} from '../../order/api';
import type {OrderStockError} from '../../order/duck';
import PendingModal from '../../order/components/modals/PendingModal';

import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import type {RootState} from '../../index';
import type {Dispatch} from 'redux';
import {actions as OrderAction} from '../../order/duck';
import StockErrorsModal from '../../order/components/modals/StockErrorsModal';
import {useHistory} from 'react-router-dom';
import {useIsFeatureInTier} from '../../../utils/component';
import CancelModal from '../../order/components/modals/CancelModal';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import InvoiceDocumentBadge from './InvoiceDocumentBadge';
import FromPreOrderTooltip from './FromPreOrderTooltip/FromPreOrderTooltip';
import {canGoPrevOrderState, getAvailableOrderStates} from '../../order/utils';

export default connect(
  (state: RootState) => ({
    orderStockErrors: state.order.orderStockErrors,
    fetchOrderStockErrorsSuccess: state.order.fetchOrderStockErrorsSuccess,
    isSaving: state.order.isSaving,
    paymentMethod: state.paymentMethodConfig.paymentMethod,
    currency: state.currency.currency,
    collaborators: state.accountsConfig.collaborators,
    updateOrderStateError: state.order.updateOrderStateError,
    currentOrderId: state.order.orderId,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...OrderAction,
      },
      dispatch
    )
)(OrderItem);

type Props = {
  order: Order,
  setTierRestrictedModal: (bool: boolean) => void,
  updateOrderState: ({orderRequest: OrderRequest}) => void,
  currency?: Currency,
  updatePreOrderState: ({orderRequest: OrderRequest}) => void,
  isSaving: boolean,
  fetchOrderStockErrors: ({orderId: string}) => void,
  fetchOrderStockErrorsSuccess: boolean,
  orderStockErrors: OrderStockError[],
  paymentMethod?: PaymentMethod,
  collaborators?: Collaborator[],
  updateOrderCollaborator: ({orderRequest: OrderRequest}) => void,
  tempCollaborators: {[key: string]: any},
  fetchCollaboratorInfo: ({collaboratorInfoRequest: Object}) => void,
  stockSettings: StoreStockSettings,
  updateOrderStateError?: string,
  currentOrderId?: string,
};

const useStyles = accentColor =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 48,
      padding: theme.spacing(0, 3),
      width: '100%',
      color: colors.grey700,
      [theme.breakpoints.down('sm')]: {
        padding: '.5rem .5rem .5rem 1rem',
        alignItems: 'flex-start',
        minHeight: 128,

        position: 'relative',
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#F8F8FA',
        },
        borderRadius: '15px',
        boxShadow:
          '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      },
      overflow: 'hidden',
    },
    leftBorder: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: 5,
      height: '130%',
      backgroundColor: accentColor && accentColor.main,
    },
    orderIdItem: {
      minWidth: 180,
      flex: '2 1',
      display: 'flex',
      flexWrap: 'nowrap',
    },
    itemColumn: {
      // flex: '2 1',
      width: '14%',
      whiteSpace: 'nowrap',
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    invoiceColumn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '14%',
    },
    manualOrderBadge: {
      width: 'fit-content',
      background: colors.grey200,
      borderRadius: '4px',
      padding: '2px 6px 2px 6px',
      color: colors.grey700,
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    itemFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    },

    collaboratorDropdown: {
      // marginRight: theme.spacing(2),
      // width: 'calc(100% - 24px)',
      width: 'calc(100% - 16px)',
    },
    mobileContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      height: '100%',
      gap: '0.25rem',
    },
    statePill: {
      textTransform: 'unset',
      width: 80,
      fontWeight: 500,
      borderRadius: '20px',
      backgroundColor: accentColor && accentColor.main,
      borderColor: 'rgba(255, 255, 255, 0.25) !important',
      padding: '1px 0',
      // textAlign: 'center',
      '&:hover': {
        backgroundColor: accentColor && accentColor.main,
        filter: 'brightness(95%)',
      },
    },
    normalStatePill: {
      width: 96,
      color: 'white',
      backgroundColor: accentColor && accentColor.main,
      borderRadius: '12px',
      padding: '1px 0',
      textAlign: 'center',
    },
    arrowButton: {
      minWidth: 0,
      backgroundColor: accentColor && accentColor.main,
      borderColor: 'rgba(255, 255, 255, 0.25) !important',
      borderRadius: '20px',
      padding: '1px 4px',
      '&:hover': {
        backgroundColor: accentColor && accentColor.main,
        filter: 'brightness(95%)',
      },
    },
    arrowTooltip: {
      borderRadius: '6px',
      background: colors.grey300,
      color: colors.grey800,
    },
    hideArrowTooltip: {
      display: 'none',
    },
    arrowTooltipArrow: {
      color: colors.grey300,
    },
    noClick: {
      cursor: 'default',
      '&:hover': {
        filter: 'unset',
      },
    },
    statePillMobile: {
      position: 'absolute',
      top: '.5rem',
      right: '.5rem',
    },
    orderId: {
      color: colors.grey600,
      fontSize: '0.875rem',
    },
    name: {
      color: colors.grey800,
      fontSize: '1rem',
      fontWeight: 600,
      marginTop: 2,
    },
    description: {
      color: colors.grey800,
      fontSize: '0.875rem',
    },
    receipt: {
      color: '#00B382',
      padding: 2,
      fontSize: '0.875rem',
      [theme.breakpoints.up('md')]: {
        padding: 'unset',
        marginLeft: 'auto',
        marginRight: theme.spacing(6),
      },
    },
    statesMenuPaper: {
      margin: '8px auto',
      maxWidth: '100%',
      borderRadius: '8px',
      boxShadow: '0px 2px 18px rgba(25, 41, 48, 0.17)',
      // [theme.breakpoints.down('xs')]: {
      //   maxWidth: 'calc(100% - 32px)',
      // },
    },
    statesMenu: {
      padding: 0,
    },
    statesMenuItem: {
      // width: 120,
      padding: '0px 10px',
      fontSize: '0.875rem',
      maxWidth: '100%',
      // [theme.breakpoints.down('xs')]: {
      //   width: '400px',
      // },
    },
    copyLinkButton: {
      borderWidth: '2px',
      padding: '0px 15px',
    },
    overflowText: {
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }));

const EMPTY_COLLABORATOR_LABEL = 'General';

function OrderItem(props: Props) {
  const {
    order,
    setTierRestrictedModal,
    updateOrderState,
    currency: propsCurrency,
    updatePreOrderState,
    isSaving,
    fetchOrderStockErrors,
    fetchOrderStockErrorsSuccess,
    orderStockErrors,
    // paymentMethod,
    collaborators,
    updateOrderCollaborator,
    fetchCollaboratorInfo,
    tempCollaborators,
    stockSettings,
    updateOrderStateError,
    currentOrderId,
  } = props;

  const history = useHistory();

  const currency = order?.invoice?.currency || propsCurrency;
  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');

  const isPreOrder = order.type === 'pre-order';

  const canGoToPrevState =
    order && canGoPrevOrderState(order, order?.taxInvoiceData);

  const availableOrderStates =
    order && getAvailableOrderStates(order, order?.taxInvoiceData);

  const orderStateIndex = useMemo(() => {
    return (
      order.orderState && ORDER_STATES.findIndex(s => s === order.orderState)
    );
  }, [order]);

  const accentColor =
    !isPreOrder && order && order.orderState
      ? ORDER_STATE_COLORS[order.orderState] || {}
      : {main: colors.purple3};

  const stateString = !isPreOrder
    ? ORDER_STATE_STRINGS[order && order.orderState]
    : 'Pre Pedido';

  const dateString = useMemo(
    () =>
      new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(order.createdAt),
    [order.createdAt]
  );

  const classes = useStyles(accentColor)();
  const mobile = useMediaQuery('(max-width:960px)');

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleClickChangeStateButton = () => {
    setOpen(prevOpen => !prevOpen);
  };
  const handleCloseStatesMenu = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const handlePendingModalClose = useCallback(
    () => setPendingModalOpen(false),
    []
  );

  const updateOrderStateWithErrorCheck = useCallback(
    (orderRequest: OrderRequest) => {
      if (!orderRequest) return;

      const shouldCheckStock =
        stockSettings?.setting?.orderState === orderRequest.state;

      if (!shouldCheckStock) {
        updateOrderState({orderRequest});
        return;
      }

      const isPreOrder = orderRequest.orderCreationType === 'PRE_ORDER';

      fetchOrderStockErrors({orderId: orderRequest.id, isPreOrder});
      setUpdateOrderStateRequest({orderRequest});
    },
    [fetchOrderStockErrors, stockSettings, updateOrderState]
  );

  const handleUpdateOrderState = useCallback(
    (state: OrderState) => {
      if (isPreOrder) {
        updatePreOrderState({
          orderRequest: {
            id: order.id,
            state: state,
          },
        });
      } else {
        const orderRequest = {
          id: order.id,
          state: state,
          previousState: order.orderState,
          orderCreationType: order.orderCreationType,
        };

        const shouldCheckStock = stockSettings?.setting?.orderState === state;

        if (shouldCheckStock) {
          updateOrderStateWithErrorCheck(orderRequest);
        } else {
          updateOrderState({orderRequest});
        }
      }
    },
    [
      isPreOrder,
      order,
      stockSettings,
      updateOrderState,
      updateOrderStateWithErrorCheck,
      updatePreOrderState,
    ]
  );

  const storeHasOrderStatusChangeFeature = useIsFeatureInTier(
    'ORDER_STATUS_CHANGE'
  );

  const hasOrderStatusChangeFeature = storeHasOrderStatusChangeFeature;

  const paymentMethodCard = useMemo(() => {
    return (
      order &&
      order.invoice &&
      order.invoice.paymentMethod &&
      order.invoice.paymentMethod === 'CARD'
    );
  }, [order]);

  const handleChangeState = useCallback(
    (state: OrderState) => {
      if (!storeHasOrderStatusChangeFeature) {
        setTierRestrictedModal(true);
      } else if (state) {
        // if (paymentMethod && paymentMethod.enabled) {
        if (order && order.orderState)
          switch (order.orderState) {
            case 'PENDING':
            case 'CONFIRMED':
              switch (state) {
                case 'PAID':
                  if (!paymentMethodCard) setPendingModalOpen(true);
                  break;
                default:
                  handleUpdateOrderState(state);
                  break;
              }
              break;
            default:
              handleUpdateOrderState(state);
              return;
          }
        // } else {
        //   setMessageError(
        //     ' Método de pago no está habilitado por favor actualizarlo para continuar.'
        //   );
        // }
        // }
      }
    },
    [
      handleUpdateOrderState,
      order,
      paymentMethodCard,
      setTierRestrictedModal,
      storeHasOrderStatusChangeFeature,
    ]
  );

  const [
    updateOrderStateRequest,
    setUpdateOrderStateRequest,
  ] = useState<Object>();

  const [openStockErrorsModal, setOpenStockErrorsModal] = useState(false);
  useEffect(() => {
    if (fetchOrderStockErrorsSuccess && updateOrderStateRequest) {
      if (orderStockErrors && orderStockErrors.length) {
        setOpenStockErrorsModal(true);
      } else {
        updateOrderState(updateOrderStateRequest);
        setUpdateOrderStateRequest();
      }
    }
    setPendingModalOpen(false);
  }, [
    fetchOrderStockErrorsSuccess,
    orderStockErrors,
    updateOrderState,
    updateOrderStateRequest,
  ]);

  useEffect(() => {
    if (!order) return;
    if (order.id !== currentOrderId) return;
    if (!updateOrderStateError) return;
    if (
      updateOrderStateError === 'NOT_ENOUGH_PRODUCT_VARIANT_STOCK_ERROR' ||
      updateOrderStateError === 'NOT_ENOUGH_PRODUCT_STOCK_ERROR'
    ) {
      fetchOrderStockErrors({orderId: order.id, isPreOrder});
      setOpenStockErrorsModal(true);
    }
  }, [
    currentOrderId,
    fetchOrderStockErrors,
    isPreOrder,
    order,
    updateOrderStateError,
  ]);

  useEffect(() => {
    if (order && order.collaboratorId) {
      const collaboratorId = order.collaboratorId;
      if (tempCollaborators && !tempCollaborators[collaboratorId]) {
        fetchCollaboratorInfo({
          collaboratorInfoRequest: {collaboratorId: order.collaboratorId},
        });
      }
    }
  }, [fetchCollaboratorInfo, order, tempCollaborators]);

  const collaboratorName = useMemo(() => {
    if (order && order.collaboratorId) {
      if (
        tempCollaborators &&
        tempCollaborators[order.collaboratorId] &&
        tempCollaborators[order.collaboratorId].data
      ) {
        const cInfo = tempCollaborators[order.collaboratorId].data;
        return cInfo.firstName + ' ' + cInfo.lastName;
      }
      return order.collaboratorId;
    }
  }, [order, tempCollaborators]);

  const handleStockErrorsConfirm = () => {
    if (updateOrderStateRequest) {
      updateOrderState(updateOrderStateRequest);
      setUpdateOrderStateRequest();
    }
  };

  const orderStatePill = useMemo(() => {
    if (isPreOrder) {
      return (
        <div
          className={`${classes.normalStatePill} ${
            mobile ? classes.statePillMobile : ''
          }`}
        >
          {stateString}
        </div>
      );
    }
    const previousStateAvailable =
      ORDER_STATES[orderStateIndex - 1] &&
      availableOrderStates?.statesToRevert.includes(
        ORDER_STATES[orderStateIndex - 1]
      );
    const nextStateAvailable =
      ORDER_STATES[orderStateIndex + 1] &&
      availableOrderStates?.statesToProceed.includes(
        ORDER_STATES[orderStateIndex + 1]
      );
    return (
      <ButtonGroup
        size="small"
        variant="contained"
        color="primary"
        disableElevation
        className={`${mobile ? classes.statePillMobile : ''}`}
      >
        <Tooltip
          open={!previousStateAvailable ? false : undefined}
          classes={{
            arrow: classes.arrowTooltipArrow,
            tooltip: classes.arrowTooltip,
          }}
          title={
            ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex - 1]]
              ? `Anterior estado - ${
                  ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex - 1]]
                }`
              : ''
          }
          arrow
        >
          <Button
            className={`${classes.arrowButton} ${
              !previousStateAvailable ? classes.noClick : ''
            }`}
            onMouseDown={e => {
              e.stopPropagation();
            }}
            onClick={e => {
              e.stopPropagation();
              handleChangeState(ORDER_STATES[orderStateIndex - 1]);
            }}
            disabled={!canGoToPrevState || !storeHasOrderStatusChangeFeature}
          >
            <ArrowBackIosRounded
              style={{
                fontSize: '0.75rem',
                color: !previousStateAvailable ? 'transparent' : undefined,
              }}
            />
          </Button>
        </Tooltip>
        <Button
          ref={anchorRef}
          className={`${classes.statePill} ${
            !previousStateAvailable && !nextStateAvailable
              ? classes.noClick
              : ''
          }`}
          onMouseDown={e => {
            e.stopPropagation();
          }}
          onClick={e => {
            e.stopPropagation();
            handleClickChangeStateButton();
          }}
          disabled={!storeHasOrderStatusChangeFeature}
        >
          {stateString}
        </Button>
        <Tooltip
          open={!nextStateAvailable ? false : undefined}
          classes={{
            arrow: classes.arrowTooltipArrow,
            tooltip:
              ORDER_STATES[orderStateIndex + 1] === 'CANCELLED'
                ? classes.hideArrowTooltip
                : classes.arrowTooltip,
          }}
          title={
            ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex + 1]] &&
            ORDER_STATES[orderStateIndex + 1] !== 'CANCELLED'
              ? `Siguiente estado - ${
                  ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex + 1]]
                }`
              : null
          }
          arrow
        >
          <Button
            disableRipple={!nextStateAvailable}
            className={`${classes.arrowButton} ${
              !nextStateAvailable ? classes.noClick : ''
            }`}
            onMouseDown={e => {
              e.stopPropagation();
            }}
            onClick={e => {
              e.stopPropagation();
              if (nextStateAvailable)
                handleChangeState(ORDER_STATES[orderStateIndex + 1]);
            }}
            disabled={!storeHasOrderStatusChangeFeature}
          >
            <ArrowForwardIosRounded
              style={{
                fontSize: '0.75rem',
                color: !nextStateAvailable ? 'transparent' : undefined,
              }}
            />
          </Button>
        </Tooltip>
      </ButtonGroup>
    );
  }, [
    availableOrderStates,
    canGoToPrevState,
    classes.arrowButton,
    classes.arrowTooltip,
    classes.arrowTooltipArrow,
    classes.hideArrowTooltip,
    classes.noClick,
    classes.normalStatePill,
    classes.statePill,
    classes.statePillMobile,
    handleChangeState,
    isPreOrder,
    mobile,
    orderStateIndex,
    stateString,
    storeHasOrderStatusChangeFeature,
  ]);

  const openReceipt = (e, url) => {
    e.preventDefault();
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const [shippingAddress, setShippingAddress] = useState('');

  useEffect(() => {
    if (order && order.shippingDetail) {
      const {shippingDetail} = order;
      const {
        address1,
        admin1 = '',
        admin2 = '',
        admin3 = '',
      } = getAddressFromShippingDetail(shippingDetail);
      setShippingAddress(address1);
      const admins = formatAddressString(admin1, admin2, admin3);
      if (admin1) setShippingAddress(admins);
    }
  }, [order, shippingAddress]);

  const itemsAmount: number = useMemo((): number => {
    if (isPreOrder && order.items) {
      let result: number = 0;
      order.items.forEach(i => {
        result += i.quantity;
      });
      return result;
    } else return 0;
  }, [isPreOrder, order.items]);

  const [copiedLinkTooltip, setCopiedLinkTooltip] = useState(false);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const handleCancelModalClose = () => setCancelModalOpen(false);
  const handleCancel = useCallback(() => {
    if (hasOrderStatusChangeFeature) setCancelModalOpen(true);
  }, [hasOrderStatusChangeFeature]);

  const invoiceReceiptImage =
    order?.receipts && order.receipts.length > 0
      ? getImageUrl(order.receipts[0].id)
      : null;

  const copyPreOrderLinkButton = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <ClickAwayListener onClickAway={() => setCopiedLinkTooltip(false)}>
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          onClose={() => setCopiedLinkTooltip(false)}
          open={copiedLinkTooltip}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          leaveDelay={300}
          title="Enlace copiado"
        >
          <div>
            <CustomButton
              variant="outlined"
              className={classes.copyLinkButton}
              startIcon={<FileCopyOutlined />}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                const textCopied = `${document.location.protocol}//${document.location.host}/checkout/information?po_id=${order.id}&po_token=${order.token}`;
                navigator &&
                  navigator.clipboard &&
                  navigator.clipboard
                    .writeText(textCopied)
                    .then(() => {
                      setCopiedLinkTooltip(true);
                    })
                    .catch(e => console.error(e));
              }}
            >
              Copiar link de pre pedido
            </CustomButton>
          </div>
        </Tooltip>
      </ClickAwayListener>
      <Tooltip
        classes={{
          arrow: classes.arrowTooltipArrow,
          tooltip: classes.arrowTooltip,
        }}
        style={{marginLeft: 8}}
        title="*Envía este link a tu cliente para que llene sus datos y proceda a pagar."
        arrow
      >
        <InfoOutlined />
      </Tooltip>
    </div>
  );

  const userHasOrderEditCollaboratorFeature = useIsFeatureInTier(
    'ORDER_EDIT_COLLABORATOR'
  );
  const handleChangeOrderCollaborator = (orderId, collaboratorId) => {
    if (collaboratorId === EMPTY_COLLABORATOR_LABEL) return;
    if (!userHasOrderEditCollaboratorFeature) return;
    updateOrderCollaborator({
      orderRequest: {
        id: orderId,
        collaboratorId: collaboratorId,
      },
    });
  };

  const isFromPreOrder = order.orderCreationType === 'PRE_ORDER';

  return (
    <>
      {mobile ? (
        <div
          className={classes.root}
          onClick={() => history.push(`/orders/${order.id}`)}
        >
          <div className={classes.leftBorder} />
          <div className={classes.mobileContent}>
            {orderStatePill}
            <div
              className={classes.orderId}
              style={{display: 'flex', alignItems: 'center', gap: '2px'}}
            >
              {(isFromPreOrder || isPreOrder) && (
                <FromPreOrderTooltip isPreOrder={isPreOrder} />
              )}{' '}
              <p>{`Nº ${isPreOrder ? 'Pre ' : ''}Orden ${order.id}`}</p>
            </div>
            {order.customer && !isPreOrder && (
              <Typography className={classes.name}>
                {order.customer.firstName + ' ' + order.customer.lastName}
              </Typography>
            )}
            {order.invoice && (
              <Typography className={classes.description}>
                {commaFormat(order.invoice.detail.total, currency?.symbol)} -{' '}
                {mapPaymentMethodName(order.invoice.paymentMethod)}
              </Typography>
            )}
            {invoiceReceiptImage && (
              <CustomButton
                className={classes.receipt}
                startIcon={<AttachFile />}
                onClick={e => openReceipt(e, invoiceReceiptImage)}
              >
                Ver Comprobante
              </CustomButton>
            )}
            {isPreOrder && (
              <>
                <Typography className={classes.description}>
                  {dateString}
                </Typography>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography className={classes.description}>
                    {order.subTotal &&
                      commaFormat(order.subTotal, currency && currency.symbol)}
                  </Typography>
                  <Typography className={classes.description}>
                    {itemsAmount} producto{itemsAmount > 1 && 's'}
                  </Typography>
                </div>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  {copyPreOrderLinkButton}
                  <IconButton
                    // className={classes.receipt}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                  >
                    <DeleteOutline color="error" />
                  </IconButton>
                </div>
              </>
            )}
            {!isPreOrder && (
              <footer className={classes.itemFooter}>
                <InvoiceDocumentBadge
                  order={order}
                  taxInvoiceList={order.taxInvoiceData?.taxInvoiceList ?? []}
                  taxInvoiceInformation={
                    order.taxInvoiceData?.taxInvoiceInformation
                  }
                />
                <div className={classes.manualOrderBadge}>
                  {{DEFAULT: 'WEB', MANUAL: 'MANUAL', PRE_ORDER: 'MANUAL'}[
                    order.orderCreationType
                  ] || ''}
                </div>
              </footer>
            )}
          </div>
        </div>
      ) : (
        <div
          className={classes.root}
          onClick={() => history.push(`/orders/${order.id}`)}
        >
          <div className={classes.itemColumn}>
            {(isFromPreOrder || isPreOrder) && (
              <FromPreOrderTooltip isPreOrder={isPreOrder} />
            )}
            <Typography>{order.id}</Typography>
          </div>
          <Typography className={classes.itemColumn} style={{width: '10%'}}>
            {dateString}
          </Typography>
          {!isPreOrder && (
            <div className={classes.itemColumn} style={{width: '12%'}}>
              <div className={classes.manualOrderBadge}>
                {{DEFAULT: 'WEB', MANUAL: 'MANUAL', PRE_ORDER: 'MANUAL'}[
                  order.orderCreationType
                ] || ''}
              </div>
            </div>
          )}
          {!isPreOrder &&
            (userHasOrderEditCollaboratorFeature ? (
              <div className={classes.itemColumn} style={{width: '18%'}}>
                <CustomSelect
                  className={classes.collaboratorDropdown}
                  // label="Colaborador"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  value={order.collaboratorId || EMPTY_COLLABORATOR_LABEL}
                  onChange={e =>
                    handleChangeOrderCollaborator(order.id, e.target.value)
                  }
                  disabled={!userHasOrderEditCollaboratorFeature}
                >
                  {collaborators &&
                    collaborators.map(collaborator => (
                      <MenuItem value={collaborator.id} key={collaborator.id}>
                        {collaborator.firstName + ' ' + collaborator.lastName}
                      </MenuItem>
                    ))}
                  <MenuItem value={EMPTY_COLLABORATOR_LABEL}>
                    {EMPTY_COLLABORATOR_LABEL}
                  </MenuItem>
                </CustomSelect>
              </div>
            ) : (
              <div className={classes.itemColumn} style={{width: '18%'}}>
                <Typography className={classes.itemColumn}>
                  {collaboratorName || ''}
                </Typography>
              </div>
            ))}
          {order.customer && !isPreOrder && (
            <Typography
              className={`${classes.itemColumn} ${classes.overflowText}`}
              style={{width: '16%'}}
            >
              {order.customer.firstName + ' ' + order.customer.lastName}
            </Typography>
          )}
          {order.invoice && !isPreOrder && (
            <>
              <div className={classes.itemColumn}>{orderStatePill}</div>

              <Typography className={classes.itemColumn}>
                {commaFormat(order.invoice.detail.total, currency?.symbol)}
              </Typography>
            </>
          )}
          {isPreOrder && (
            <>
              <Typography className={classes.itemColumn}>
                {order.subTotal &&
                  commaFormat(order.subTotal, currency && currency.symbol)}
              </Typography>
              <Typography className={classes.itemColumn}>
                {itemsAmount}
              </Typography>
              {copyPreOrderLinkButton}
            </>
          )}
          {hasTaxInvoiceFeature && (
            <div className={classes.invoiceColumn}>
              <InvoiceDocumentBadge
                order={order}
                taxInvoiceList={order.taxInvoiceData?.taxInvoiceList ?? []}
                taxInvoiceInformation={
                  order.taxInvoiceData?.taxInvoiceInformation
                }
              />
            </div>
          )}
          <div style={{display: 'flex', alignItems: 'center'}}>
            {isPreOrder && (
              <IconButton
                // className={classes.receipt}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                <DeleteOutline color="error" />
              </IconButton>
            )}
            <ArrowForwardIosRounded
              style={{color: colors.grey600}}
              fontSize="small"
            />
          </div>
        </div>
      )}
      {!isPreOrder && (
        <>
          <PendingModal
            open={pendingModalOpen}
            handleClose={handlePendingModalClose}
            order={order}
            updateOrderStateWithErrorCheck={updateOrderStateWithErrorCheck}
            isSaving={isSaving}
          />
          <StockErrorsModal
            open={openStockErrorsModal}
            order={order}
            updateOrderStateRequest={updateOrderStateRequest}
            orderStockErrors={orderStockErrors}
            handleConfirm={handleStockErrorsConfirm}
            handleClose={() => setOpenStockErrorsModal(false)}
          />
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{
              zIndex: 1,
            }}
          >
            {({TransitionProps, placement}) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper className={classes.statesMenuPaper}>
                  <ClickAwayListener onClickAway={handleCloseStatesMenu}>
                    <MenuList
                      autoFocusItem={open}
                      className={classes.statesMenu}
                    >
                      {availableOrderStates &&
                        canGoToPrevState &&
                        availableOrderStates.statesToRevert.map(state => (
                          <MenuItem
                            key={`${state}-menu-item`}
                            className={classes.statesMenuItem}
                            onClick={e => {
                              handleCloseStatesMenu(e);
                              handleChangeState(state);
                            }}
                          >
                            <div
                              style={{
                                height: 14,
                                width: 14,
                                margin: '8px 10px 8px 0',
                                borderRadius: '50%',
                                backgroundColor: ORDER_STATE_COLORS[state].main,
                              }}
                            />
                            {ORDER_STATE_STRINGS[state]}
                          </MenuItem>
                        ))}
                      {availableOrderStates &&
                        availableOrderStates?.statesToProceed.map(state => (
                          <MenuItem
                            key={`${state}-menu-item`}
                            className={classes.statesMenuItem}
                            onClick={e => {
                              handleCloseStatesMenu(e);
                              handleChangeState(state);
                            }}
                          >
                            <div
                              style={{
                                height: 14,
                                width: 14,
                                margin: '8px 10px 8px 0',
                                borderRadius: '50%',
                                backgroundColor: ORDER_STATE_COLORS[state].main,
                              }}
                            />
                            {ORDER_STATE_STRINGS[state]}
                          </MenuItem>
                        ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      )}
      <CancelModal
        open={cancelModalOpen}
        handleClose={handleCancelModalClose}
        order={order}
        handleUpdateOrderState={handleUpdateOrderState}
      />
    </>
  );
}

OrderItem.propTypes = {
  order: PropTypes.object.isRequired,
  setTierRestrictedModal: PropTypes.func,
  updateOrderState: PropTypes.func.isRequired,
  currency: PropTypes.object,
  updatePreOrderState: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  paymentMethod: PropTypes.object,
  fetchOrderStockErrors: PropTypes.func.isRequired,
  fetchOrderStockErrorsSuccess: PropTypes.bool,
  orderStockErrors: PropTypes.array,
  collaborators: PropTypes.array,
  updateOrderCollaborator: PropTypes.func.isRequired,
  tempCollaborators: PropTypes.object.isRequired,
  fetchCollaboratorInfo: PropTypes.func.isRequired,
};
